import { z } from "zod"
import { Prisma } from "db"
import isUserTestGroupsNotOverlapping from "./services/isUserTestGroupsNotOverlapping"
import { TargetSystem } from "app/orders/validations"

export enum Dir {
  LTR = "ltr",
  RTL = "rtl",
}

export enum PipelineOfferType {
  BASIC = "BASIC",
  PREMIUM = "PREMIUM",
  SHOPPING_LIST = "SHOPPING_LIST",
  SHOPPING_LIST_AND_PREMIUM = "SHOPPING_LIST_AND_PREMIUM",
  BASIC_ANNUAL = "BASIC_ANNUAL",
  CHAT = "CHAT",
  WORKOUTS = "WORKOUTS",
  BASIC_EXIT_INTENT = "BASIC_EXIT_INTENT",
  PREMIUM_EXIT_INTENT = "PREMIUM_EXIT_INTENT",
}

export enum AnalyticsProvider {
  GOOGLE = "google",
  CPPV3 = "cppv3",
}

export enum PipelineStatus {
  ALL = "All",
  ACTIVE = "active",
  DRAFT = "draft",
  NO_LONGER_ACTIVE = "no_longer_active",
  DEV_TEST = "dev_test",
}

const zdecimal = z
  .number()
  .or(z.string().regex(/^\d+(\.\d+)?$/, "Expected number"))
  .or(z.instanceof(Prisma.Decimal))

export const PipelineAbandonedBasketConfig = z.object({
  id: z.number().optional(),
  pipelineId: z.number().optional(),
  userTestGroups: z.array(z.number()),
  abandonedBasketEmailDelay: z.number(),
  abandonedBasketEmailTemplateId: z.number(),
  abandonedBasketPipelineId: z.number().nullable().optional(),
  secondAbandonedBasketEmailDelay: z.number().optional(),
  secondAbandonedBasketEmailTemplateId: z.number().nullable().optional(),
  secondAbandonedBasketPipelineId: z.number().nullable().optional(),
  thirdAbandonedBasketEmailDelay: z.number().optional(),
  thirdAbandonedBasketEmailTemplateId: z.number().nullable().optional(),
  thirdAbandonedBasketPipelineId: z.number().nullable().optional(),
  fourthAbandonedBasketEmailDelay: z.number().optional(),
  fourthAbandonedBasketEmailTemplateId: z.number().nullable().optional(),
  fourthAbandonedBasketPipelineId: z.number().nullable().optional(),
})

export const PipelineOffer = z
  .object({
    type: z.nativeEnum(PipelineOfferType),
    brandOfferId: z.number().nullable().optional(),
    priceProductObjectId: z.string().nullable().optional(),
    exitIntentPriceProductObjectId: z.string().nullable().optional(),
    currencyId: z.number(),
    customData: z.object({}).passthrough().nullable(),
    prices: z.object({}).passthrough().array().optional(),
  })
  .refine(
    (offer) => offer.brandOfferId || offer.priceProductObjectId,
    "Brand Offer or Price-Product Object required"
  )

export const PipelinePrimerMetadata = z.object({
  key: z.string(),
  value: z.string(),
})

const pipelineName = z
  .string()
  .regex(/^[a-z0-9\-]+$/, 'Must contain only a-z, 0-9, "-" characters')
  .refine((name) => name !== "test", { message: 'Pipeline name cannot be "test"' })

export const CreatePipeline = z.object({
  name: pipelineName,
  description: z.string().trim().optional().nullable(),
  geographyId: z.number(),
  languageId: z.number(),
  brandId: z.number(),
  githubRepositoryUrl: z.string().trim().url(),
  githubRepositoryRef: z.string().trim(),
  githubRepositoryPath: z.string().trim().optional(),
  brandOfferId: z.number().int(),
  currencyId: z.number(),
  standardDisplayPrice: zdecimal,
  standardDisplayPriceString: z.string(),
  exitIntentDisplayPrice: zdecimal,
  exitIntentDisplayPriceString: z.string(),
  termsOfServiceUrl: z.string().trim().url(),
  privacyPolicyUrl: z.string().trim().url(),
  cookiesPolicyUrl: z.string().trim().url(),
  paymentMethodIds: z.number().array(),
  pipelineAbandonedBasketConfigs: z
    .array(PipelineAbandonedBasketConfig)
    .refine(isUserTestGroupsNotOverlapping, "User test groups should not overlap!"),
  metaTitle: z.string().trim().optional().nullable(),
  metaDescription: z.string().trim().optional().nullable(),
  firstPageTitle: z.string().trim().optional().nullable(),
  measurementSystem: z.string(),
  dir: z.string(),
  blogLink: z.string().trim().optional().nullable(),
  analyticsProvider: z.string(),
  facebookPixelId: z.string().trim().optional().nullable(),
  facebookPixelAccessToken: z.string().trim().optional().nullable(),
  targetSystem: z.string(),
  validateEmailDomain: z.boolean(),
  expressPlanDeliveryEnabled: z.boolean(),
  expressPlanDeliveryUserTestGroups: z.array(z.number()),
  expressPlanDeliveryTimerSeconds: z.number().optional(),
  cookiePolicyBannerEnabled: z.boolean(),
  npsPreloadSessionEnabled: z.boolean(),
  useEmailServiceVerification: z.boolean(),
  rejectIncomingTraffic: z.boolean(),
  primerSdkVersion: z.string().trim().optional().nullable(),
  segmentEvents: z.any(),
  series: z.string().optional(),
  klaviyoConfigId: z.number().nullable().optional(),
  funnelfoxFunnelId: z.string().trim().optional().nullable(),
  isUsingPublicOrderApi: z.boolean(),
})

export const UpdatePipeline = CreatePipeline.extend({
  id: z.number(),
  pipelinePages: z.array(
    z.object({
      id: z.number(),
      path: z.string().regex(/^[a-z0-9\_\-]+$/),
      clickthroughPageKey: z.string().trim().optional().nullable(),
      funnelKeyViewsMarkers: z.array(z.string()).optional().nullable(),
      name: z.string().trim().optional().nullable(),
      versionName: z.string().trim().optional().nullable(),
    })
  ),
  offers: z.array(PipelineOffer),
  configValues: z.object({}).passthrough().nullable().optional(),
  importFile: z.any().nullable(),
  assets: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        url: z.string(),
      })
    )
    .optional(),
  variant: z.string().nullable().optional(),
  analyticsProvider: z.string(),
  primerMetadata: z.array(PipelinePrimerMetadata).nullable().optional(),
  frontendCustomFields: z.object({}).passthrough().nullable().optional(),
  klaviyoConfigId: z.number().nullable().optional(),
  status: z.string().nullable(),
  loginUrl: z.string().nullable(),
})
  .refine((pipeline) => {
    if (!pipeline.offers?.length || pipeline.targetSystem !== TargetSystem.API_DIET) return true

    return pipeline.offers.every((offer) => !!offer.brandOfferId)
  }, '"api-diet" pipelines must have only "api-diet" offers')
  .refine((pipeline) => {
    if (!pipeline.offers?.length || pipeline.targetSystem !== TargetSystem.NNP_2) return true

    return pipeline.offers.every((offer) => !!offer.priceProductObjectId)
  }, '"nnp-2" pipelines must have only PPO offers')

export const BulkUpdatePipelines = z
  .object({
    pipelineIds: z.number().array(),
    githubRepositoryUrl: z.string().url().optional(),
    githubRepositoryRef: z.string().optional(),
    paymentMethodIds: z.number().array().optional(),
    pipelineAbandonedBasketConfigs: z.array(PipelineAbandonedBasketConfig),
  })
  .refine((formData) => {
    return Object.keys(formData).length > 1
  }, "At least one field must be set!")

export const ConfigurePipelinePages = z.object({
  id: z.number(),
})

export const DeletePipeline = z.object({
  id: z.number(),
})

export const DeployPipeline = z.object({
  id: z.number(),
})

export const DeployPipelines = z.object({
  ids: z.array(z.number()),
})

export const PublishPipeline = z.object({
  id: z.number(),
})

export const CopyPipeline = z.object({
  id: z.number(),
  name: pipelineName,
  geographyId: z.number(),
  languageId: z.number(),
})

export const SyncFunnelKeys = z.object({
  id: z.number(),
})

export const TranslationsJson = z.object({})

export const CreateOrUpdateCustomPropertyGroup = z.object({
  id: z.number().optional(),
  properties: z.any(),
  pipelineId: z.number(),
})

export enum TranslationStatus {
  DRAFT = "DRAFT",
  ACTIVE = "ACTIVE",
}

export enum AssetStatus {
  DRAFT = "DRAFT",
  ACTIVE = "ACTIVE",
}

export enum DeploymentStatus {
  IN_PROGRESS = "IN_PROGRESS",
  FAILED = "FAILED",
  PENDING_INVALIDATION = "PENDING_INVALIDATION",
  INVALIDATION_IN_PROGRESS = "INVALIDATION_IN_PROGRESS",
  INVALIDATION_FAILED = "INVALIDATION_FAILED",
  SUCCESS = "SUCCESS",
  NOT_DEPLOYED = "NOT_DEPLOYED",
}

export enum InvalidationStatus {
  PENDING = "PENDING",
  QUEUED = "QUEUED",
  IN_PROGRESS = "IN_PROGRESS",
  FAILED = "FAILED",
  SUCCESS = "SUCCESS",
}
